import { ALL_TECHS, Cat, KINDS, hasKind, kindIndex, techIndex } from '../../../gameplay/registry';
import { C } from '../../../gameplay/types';
import { FX_ONE, isqrt } from '../fixed';
import { MAX_COMMAND_IDS, type Command, type CommandEnvelope, type FormationType } from '../commands';
import { FORMATION_IDS, Order, STANCE_IDS, UState, type World } from '../world';
import { canPlace, orderBuild } from './construction';
import { canCapture } from './capture';
import { mayEnter, orderUnload, resumeWork } from './garrison';
import { gatherableBy, isFarm, orderGather, resourceOf } from './economy';
import { setDestination, stopMoving } from './movement';
import { MAX_QUEUE, canAfford, pay, refund, techAvailability, unitAvailability } from './production';
import { MAX_WALL_TILES } from '../../../gameplay/walls';

/** Filter to live units owned by player. Returns slots. Rejects foreign/nonexistent ids. */
function ownUnits(w: World, player: number, ids: number[]): number[] {
  const out: number[] = [];
  if (!Array.isArray(ids)) return out;
  const seen = new Set<number>();
  for (const id of ids.slice(0, MAX_COMMAND_IDS)) {
    if (typeof id !== 'number') continue;
    const s = w.resolve(id);
    if (s < 0 || seen.has(s) || w.owner[s] !== player || w.cat[s] !== Cat.Unit) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

function ownBuilding(w: World, player: number, id: number): number {
  const s = w.resolve(id);
  if (s < 0 || w.owner[s] !== player || w.cat[s] !== Cat.Building) return -1;
  return s;
}

function validPoint(w: World, x: unknown, y: unknown): boolean {
  return typeof x === 'number' && typeof y === 'number' && Number.isInteger(x) && Number.isInteger(y) && x >= 0 && y >= 0 && x < w.size * FX_ONE && y < w.size * FX_ONE;
}

function clearOrder(w: World, s: number): void {
  w.target[s] = -1;
  w.ally[s] = -1;
  w.buildQueue[s] = null;
  w.groupSpeed[s] = 0;
  // A new order cancels any make-way step aside, and the walk to a rally point.
  w.yieldTimer[s] = 0;
  w.rallying[s] = 0;
}

/** Release any farm this villager was working. */
function releaseFarm(w: World, s: number): void {
  const t = w.resolve(w.target[s]);
  if (t >= 0 && isFarm(w, t) && w.worker[t] === w.idOf(s)) w.worker[t] = -1;
}

/**
 * Compute formation slot offsets for n units, given unit-vector facing (fx,fy) scaled by 1000.
 * Returns offsets in fixed-point, ordered front-to-back.
 */
export function formationOffsets(type: FormationType, n: number, fx: number, fy: number, spacing: number): [number, number][] {
  const local: [number, number][] = []; // [right, back] in spacing units *2 (to allow halves)
  if (type === 'column') {
    const cols = Math.min(3, n);
    for (let i = 0; i < n; i++) local.push([(i % cols) * 2 - (cols - 1), Math.trunc(i / cols) * 2]);
  } else if (type === 'wedge') {
    let row = 0;
    let i = 0;
    while (i < n) {
      const width = row * 2 + 1;
      for (let c = 0; c < width && i < n; c++, i++) local.push([(c - row) * 2, row * 2]);
      row++;
    }
  } else if (type === 'square') {
    // Hollow square perimeter.
    let side = 2;
    while ((side - 1) * 4 < n) side++;
    const pts: [number, number][] = [];
    for (let i = 0; i < side; i++) pts.push([i, 0]);
    for (let j = 1; j < side; j++) pts.push([side - 1, j]);
    for (let i = side - 2; i >= 0; i--) pts.push([i, side - 1]);
    for (let j = side - 2; j >= 1; j--) pts.push([0, j]);
    for (let i = 0; i < n; i++) {
      const p = pts[i % pts.length];
      local.push([p[0] * 2 - (side - 1), p[1] * 2]);
    }
  } else {
    let cols: number;
    if (type === 'box' || type === 'none') cols = Math.max(1, Math.ceil(Math.sqrt(n)));
    else cols = Math.max(1, Math.min(n, Math.ceil(n / Math.max(1, Math.ceil(Math.sqrt(n / 5))))));
    for (let i = 0; i < n; i++) {
      const row = Math.trunc(i / cols);
      const inRow = Math.min(cols, n - row * cols);
      local.push([(i % cols) * 2 - (inRow - 1), row * 2]);
    }
  }
  const sp = type === 'loose' ? spacing * 2 : spacing;
  // Right vector = (-fy, fx); back = (-fx, -fy).
  return local.map(([r, b]) => [Math.trunc(((-fy * r - fx * b) * sp) / 2000), Math.trunc(((fx * r - fy * b) * sp) / 2000)]);
}

function issueGroupMove(w: World, units: number[], x: number, y: number, attackMove: boolean, formation: FormationType): void {
  if (units.length === 0) return;
  let cx = 0;
  let cy = 0;
  for (const s of units) {
    cx += w.x[s];
    cy += w.y[s];
  }
  cx = Math.trunc(cx / units.length);
  cy = Math.trunc(cy / units.length);
  const dx = x - cx;
  const dy = y - cy;
  const d = isqrt(dx * dx + dy * dy);
  const fx = d > 0 ? Math.trunc((dx * 1000) / d) : 0;
  const fy = d > 0 ? Math.trunc((dy * 1000) / d) : 1000;
  // Melee in front, ranged and siege behind; stable ordering by slot.
  const role = (s: number) => {
    const u = KINDS[w.kind[s]].unit!;
    if (u.classes & C.Siege) return 3;
    if (u.attack && u.attack.range > 0) return u.classes & C.Cavalry ? 2 : 1;
    return 0;
  };
  const sorted = units.slice().sort((a, b) => role(a) - role(b) || a - b);
  let maxR = 0;
  let minSpeed = Infinity;
  for (const s of sorted) {
    maxR = Math.max(maxR, Math.round(KINDS[w.kind[s]].unit!.radius * FX_ONE));
    minSpeed = Math.min(minSpeed, w.players[w.owner[s]].stats[w.kind[s]].speed);
  }
  const spacing = maxR * 2 + (FX_ONE >> 2);
  const offs = units.length === 1 ? [[0, 0] as [number, number]] : formationOffsets(formation, sorted.length, fx, fy, spacing);
  const fid = FORMATION_IDS[formation];
  for (let i = 0; i < sorted.length; i++) {
    const s = sorted[i];
    releaseFarm(w, s);
    clearOrder(w, s);
    const gx = Math.max(0, Math.min(w.size * FX_ONE - 1, x + offs[i][0]));
    const gy = Math.max(0, Math.min(w.size * FX_ONE - 1, y + offs[i][1]));
    w.order[s] = attackMove ? Order.AttackMove : Order.Move;
    w.state[s] = UState.Walking;
    w.goalX[s] = gx;
    w.goalY[s] = gy;
    w.formation[s] = fid;
    w.groupSpeed[s] = fid > 0 && units.length > 1 ? minSpeed : 0;
    setDestination(w, s, gx, gy);
  }
}

/** Validate and apply one command. Returns false if rejected. */
export function applyCommand(w: World, env: CommandEnvelope): boolean {
  const player = env.player;
  if (player < 0 || player >= w.players.length) return false;
  const p = w.players[player];
  if (p.defeated) return false;
  const cmd: Command = env.cmd;
  if (!cmd || typeof cmd !== 'object') return false;

  switch (cmd.t) {
    case 'move': {
      if (!validPoint(w, cmd.x, cmd.y)) return false;
      const units = ownUnits(w, player, cmd.ids);
      const f: FormationType = cmd.formation && cmd.formation in FORMATION_IDS ? cmd.formation : 'none';
      issueGroupMove(w, units, cmd.x, cmd.y, !!cmd.attackMove, f);
      return units.length > 0;
    }
    case 'patrol': {
      if (!validPoint(w, cmd.x, cmd.y)) return false;
      const units = ownUnits(w, player, cmd.ids);
      for (const s of units) {
        releaseFarm(w, s);
        clearOrder(w, s);
        w.order[s] = Order.Patrol;
        w.state[s] = UState.Walking;
        w.homeX[s] = w.x[s];
        w.homeY[s] = w.y[s];
        w.goalX[s] = cmd.x;
        w.goalY[s] = cmd.y;
        setDestination(w, s, cmd.x, cmd.y);
      }
      return units.length > 0;
    }
    case 'attack': {
      const t = w.resolve(cmd.target);
      if (t < 0 || w.cat[t] === Cat.Resource || !w.areEnemies(player, w.owner[t])) return false;
      const units = ownUnits(w, player, cmd.ids).filter((s) => KINDS[w.kind[s]].unit!.attack);
      for (const s of units) {
        releaseFarm(w, s);
        clearOrder(w, s);
        w.order[s] = Order.Attack;
        w.target[s] = cmd.target;
        w.homeX[s] = w.x[s];
        w.homeY[s] = w.y[s];
        w.timer[s] = 0;
        w.formation[s] = 0;
        setDestination(w, s, w.x[t], w.y[t]);
      }
      return units.length > 0;
    }
    case 'gather': {
      const t = w.resolve(cmd.target);
      if (t < 0) return false;
      if (w.cat[t] === Cat.Building && w.owner[t] !== player) return false;
      const r = resourceOf(w, t);
      const units = ownUnits(w, player, cmd.ids).filter((s) => KINDS[w.kind[s]].unit!.gatherRates && (r < 0 || gatherableBy(w, s, t)));
      if (r < 0) {
        // Gathering on an unfinished farm means build it.
        if (w.cat[t] === Cat.Building && !w.built[t]) {
          for (const s of units) {
            releaseFarm(w, s);
            clearOrder(w, s);
            orderBuild(w, s, t);
          }
          return units.length > 0;
        }
        return false;
      }
      // Any number of gatherers may share one resource, farm included.
      for (const s of units) {
        releaseFarm(w, s);
        clearOrder(w, s);
        orderGather(w, s, t);
      }
      return units.length > 0;
    }
    case 'build': {
      if (typeof cmd.kind !== 'string' || !hasKind(cmd.kind)) return false;
      const ki = kindIndex(cmd.kind);
      const bdef = KINDS[ki].building;
      if (!bdef || bdef.buildable === false) return false;
      if (!Number.isInteger(cmd.tx) || !Number.isInteger(cmd.ty)) return false;
      const st = p.stats[ki];
      if (!st.enabled || p.age < bdef.age) return false;
      if (bdef.limit && p.buildingCounts[ki] >= bdef.limit) return false;
      const units = ownUnits(w, player, cmd.ids).filter((s) => KINDS[w.kind[s]].unit!.classes & C.Villager);
      if (units.length === 0) return false;
      if (!canAfford(p, st.cost)) {
        w.events.push({ e: 'notice', owner: player, text: 'Not enough resources' });
        return false;
      }
      if (!canPlace(w, ki, cmd.tx, cmd.ty, player)) return false;
      pay(p, st.cost);
      const b = w.spawnBuilding(ki, player, cmd.tx, cmd.ty, false);
      if (b < 0) {
        refund(p, st.cost);
        return false;
      }
      for (const s of units) {
        if (cmd.queue && (w.order[s] === Order.Build || w.order[s] === Order.Repair)) {
          (w.buildQueue[s] ??= []).push(w.idOf(b));
          continue;
        }
        releaseFarm(w, s);
        clearOrder(w, s);
        orderBuild(w, s, b);
      }
      return true;
    }
    case 'buildWall': {
      if (!Array.isArray(cmd.pieces) || cmd.pieces.length === 0 || cmd.pieces.length > MAX_WALL_TILES) return false;
      const units = ownUnits(w, player, cmd.ids).filter((s) => KINDS[w.kind[s]].unit!.classes & C.Villager);
      if (units.length === 0) return false;
      const placed: number[] = [];
      let short = false;
      for (const piece of cmd.pieces) {
        if (!Array.isArray(piece) || typeof piece[0] !== 'string' || !hasKind(piece[0])) return false;
        const [key, tx, ty] = piece;
        const ki = kindIndex(key);
        const bdef = KINDS[ki].building;
        if (!bdef || !(bdef.classes & C.Wall) || !Number.isInteger(tx) || !Number.isInteger(ty)) return false;
        const st = p.stats[ki];
        if (!st.enabled || p.age < bdef.age) return false;
        // Tiles already walled (e.g. a line snapped onto an existing wall) are simply skipped.
        if (!canPlace(w, ki, tx, ty, player)) continue;
        if (!canAfford(p, st.cost)) {
          short = true;
          break;
        }
        pay(p, st.cost);
        const b = w.spawnBuilding(ki, player, tx, ty, false);
        if (b < 0) {
          refund(p, st.cost);
          continue;
        }
        placed.push(w.idOf(b));
      }
      if (short) w.events.push({ e: 'notice', owner: player, text: placed.length ? 'Not enough resources: wall shortened' : 'Not enough resources' });
      if (placed.length === 0) return false;
      for (const s of units) {
        if (cmd.queue && (w.order[s] === Order.Build || w.order[s] === Order.Repair)) {
          (w.buildQueue[s] ??= []).push(...placed);
          continue;
        }
        releaseFarm(w, s);
        clearOrder(w, s);
        orderBuild(w, s, w.resolve(placed[0]));
        w.buildQueue[s] = placed.slice(1);
      }
      return true;
    }
    case 'capture': {
      const t = w.resolve(cmd.target);
      if (!canCapture(w, player, t)) return false;
      const units = ownUnits(w, player, cmd.ids).filter((s) => KINDS[w.kind[s]].unit!.classes & C.Engineer);
      for (const s of units) {
        clearOrder(w, s);
        w.order[s] = Order.Capture;
        w.target[s] = w.idOf(t);
        w.state[s] = UState.Walking;
      }
      return units.length > 0;
    }
    case 'garrison': {
      const c = w.resolve(cmd.target);
      if (w.rules < 8 || c < 0) return false;
      const units = ownUnits(w, player, cmd.ids).filter((s) => mayEnter(w, s, c));
      for (const s of units) {
        // Remember the job for Back to Work (unless already on the way in).
        if (w.order[s] !== Order.Garrison) {
          w.prevOrder[s] = w.order[s];
          w.prevTarget[s] = w.target[s];
          w.prevX[s] = w.x[s];
          w.prevY[s] = w.y[s];
        }
        releaseFarm(w, s);
        clearOrder(w, s);
        w.order[s] = Order.Garrison;
        w.target[s] = w.idOf(c);
        w.state[s] = UState.Walking;
        stopMoving(w, s);
      }
      return units.length > 0;
    }
    case 'unload': {
      if (w.rules < 8) return false;
      const ids = Array.isArray(cmd.ids) ? cmd.ids.slice(0, MAX_COMMAND_IDS) : [];
      const at = validPoint(w, cmd.x, cmd.y);
      let any = false;
      for (const id of ids) {
        const s = typeof id === 'number' ? w.resolve(id) : -1;
        if (s < 0 || w.owner[s] !== player || !w.cargo[s]) continue;
        if (w.cat[s] === Cat.Building) {
          const out = w.releaseCargo(s, at ? cmd.x! : w.rallyX[s], at ? cmd.y! : w.rallyY[s]);
          if (cmd.resume === true) for (const u of out) resumeWork(w, u);
        }
        else if (w.cat[s] === Cat.Unit) {
          clearOrder(w, s);
          orderUnload(w, s, at ? cmd.x! : w.x[s], at ? cmd.y! : w.y[s]);
        }
        any = true;
      }
      return any;
    }
    case 'repair': {
      const t = ownBuilding(w, player, cmd.target);
      if (t < 0) return false;
      // Villagers build and repair; engineers only repair finished buildings.
      const units = ownUnits(w, player, cmd.ids).filter((s) => {
        const cl = KINDS[w.kind[s]].unit!.classes;
        return cl & C.Villager || (cl & C.Engineer && w.built[t]);
      });
      for (const s of units) {
        releaseFarm(w, s);
        clearOrder(w, s);
        orderBuild(w, s, t);
        if (w.built[t]) w.order[s] = Order.Repair;
      }
      return units.length > 0;
    }
    case 'train': {
      const b = ownBuilding(w, player, cmd.building);
      if (b < 0 || !w.built[b] || typeof cmd.kind !== 'string' || !hasKind(cmd.kind)) return false;
      const ki = kindIndex(cmd.kind);
      if (unitAvailability(w, p, b, ki) !== null) return false;
      const count = Math.max(1, Math.min(5, Math.trunc(Number(cmd.count) || 1)));
      const q = (w.queues[b] ??= []);
      let added = 0;
      for (let i = 0; i < count && q.length < MAX_QUEUE; i++) {
        const cost = p.stats[ki].cost;
        if (!canAfford(p, cost)) {
          w.events.push({ e: 'notice', owner: player, text: 'Not enough resources' });
          break;
        }
        pay(p, cost);
        q.push({ kind: ki, tech: -1, progress: 0, total: p.stats[ki].trainTicks });
        added++;
      }
      return added > 0;
    }
    case 'research': {
      const b = ownBuilding(w, player, cmd.building);
      if (b < 0 || !w.built[b] || typeof cmd.tech !== 'string') return false;
      let ti: number;
      try {
        ti = techIndex(cmd.tech);
      } catch {
        return false;
      }
      if (ALL_TECHS[ti].researchedAt !== KINDS[w.kind[b]].key) return false;
      if (techAvailability(w, p, ti) !== null) return false;
      const q = (w.queues[b] ??= []);
      if (q.length >= MAX_QUEUE) return false;
      const ts = p.techStats[ti];
      if (!canAfford(p, ts.cost)) {
        w.events.push({ e: 'notice', owner: player, text: 'Not enough resources' });
        return false;
      }
      pay(p, ts.cost);
      p.researching[ti] = 1;
      q.push({ kind: -1, tech: ti, progress: 0, total: ts.ticks });
      return true;
    }
    case 'cancel': {
      const b = ownBuilding(w, player, cmd.building);
      const q = b >= 0 ? w.queues[b] : null;
      if (!q || !Number.isInteger(cmd.index) || cmd.index < 0 || cmd.index >= q.length) return false;
      const [it] = q.splice(cmd.index, 1);
      if (it.kind >= 0) refund(p, p.stats[it.kind].cost);
      else {
        refund(p, p.techStats[it.tech].cost);
        p.researching[it.tech] = 0;
      }
      return true;
    }
    case 'rally': {
      const b = ownBuilding(w, player, cmd.building);
      if (b < 0 || !validPoint(w, cmd.x, cmd.y)) return false;
      w.rallyX[b] = cmd.x;
      w.rallyY[b] = cmd.y;
      w.rallyTarget[b] = cmd.target !== undefined && w.resolve(cmd.target) >= 0 ? cmd.target : -1;
      return true;
    }
    case 'stop': {
      for (const s of ownUnits(w, player, cmd.ids)) {
        releaseFarm(w, s);
        clearOrder(w, s);
        w.order[s] = Order.None;
        w.state[s] = UState.Idle;
        stopMoving(w, s);
      }
      return true;
    }
    case 'stance': {
      const id = STANCE_IDS[cmd.stance];
      if (id === undefined) return false;
      for (const s of ownUnits(w, player, cmd.ids)) w.stance[s] = id;
      return true;
    }
    case 'guard':
    case 'follow': {
      const t = w.resolve(cmd.target);
      if (t < 0 || w.owner[t] < 0 || w.areEnemies(player, w.owner[t])) return false;
      for (const s of ownUnits(w, player, cmd.ids)) {
        if (s === t) continue;
        releaseFarm(w, s);
        clearOrder(w, s);
        w.order[s] = cmd.t === 'guard' ? Order.Guard : Order.Follow;
        w.ally[s] = cmd.target;
        w.timer[s] = 0;
      }
      return true;
    }
    case 'retreat': {
      // Retreat to nearest own town centre (or any building).
      const units = ownUnits(w, player, cmd.ids);
      let best = -1;
      let bestD = Infinity;
      if (units.length === 0) return false;
      const ux = w.x[units[0]];
      const uy = w.y[units[0]];
      for (let i = 0; i < w.highSlot; i++) {
        if (!w.alive[i] || w.cat[i] !== Cat.Building || w.owner[i] !== player || !w.built[i]) continue;
        const d = (w.x[i] - ux) * (w.x[i] - ux) + (w.y[i] - uy) * (w.y[i] - uy) - (KINDS[w.kind[i]].key === 'townCenter' ? 1e12 : 0);
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      }
      if (best < 0) return false;
      const [ex, ey] = w.exitPoint(best, ux, uy);
      issueGroupMove(w, units, ex, ey, false, 'none');
      for (const s of units) w.stance[s] = STANCE_IDS.passive;
      return true;
    }
    case 'delete': {
      const ids = Array.isArray(cmd.ids) ? cmd.ids.slice(0, MAX_COMMAND_IDS) : [];
      for (const id of ids) {
        const s = w.resolve(id);
        if (s < 0 || w.owner[s] !== player) continue;
        if (w.cat[s] === Cat.Building && !w.built[s]) {
          // Refund the unbuilt fraction of a foundation.
          const total = p.stats[w.kind[s]].buildTicks * 1000;
          const left = Math.max(0, total - w.buildProg[s]);
          const cost = p.stats[w.kind[s]].cost;
          for (let r = 0; r < 4; r++) p.res[r] += Math.trunc((cost[r] * FX_ONE * left) / total);
        }
        w.kill(s);
      }
      return true;
    }
    case 'trade': {
      const r = cmd.res;
      if (r !== 0 && r !== 1 && r !== 3) return false;
      let hasMarket = false;
      for (let i = 0; i < w.highSlot; i++) if (w.alive[i] && w.owner[i] === player && w.built[i] && KINDS[w.kind[i]].key === 'market') hasMarket = true;
      if (!hasMarket) return false;
      const price = p.prices[r];
      const bonus = p.global.tradePct;
      if (cmd.buy) {
        const cost = Math.trunc((price * 100) / (100 + bonus));
        if (p.res[2] < cost * FX_ONE) return false;
        p.res[2] -= cost * FX_ONE;
        p.res[r] += 100 * FX_ONE;
        p.prices[r] = Math.min(9999, price + 5);
      } else {
        if (p.res[r] < 100 * FX_ONE) return false;
        p.res[r] -= 100 * FX_ONE;
        p.res[2] += Math.trunc((price * 70 * (100 + bonus)) / 10000) * FX_ONE;
        p.prices[r] = Math.max(20, price - 4);
      }
      return true;
    }
    case 'autoReseed':
      p.autoReseed = !!cmd.on;
      return true;
    case 'saveGame':
      w.events.push({ e: 'saveRequested', owner: player, name: String(cmd.name).slice(0, 60) });
      return true;
    case 'resign':
      p.resigned = true;
      return true;
    default:
      return false;
  }
}

